import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonRefresher,
  IonRefresherContent,
  IonToast,
  RefresherEventDetail,
} from '@ionic/react';
import { Briefcase, FileText, Users, Lock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { ProEmptyState, ProLoadingList } from '../../components/pro/primitives';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Workspace {
  id: string;
  name: string;
  description?: string;
  bill_count: number;
  legislator_count?: number;
  can_manage: boolean;
  has_subscription: boolean;
  owner_org_name?: string;
  logo_file?: string;
  logoUrl?: string;
}

// ---------------------------------------------------------------------------
// Workspace Card Component
// ---------------------------------------------------------------------------

interface WorkspaceCardProps {
  workspace: Workspace;
  onClick: (workspace: Workspace) => void;
}

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({ workspace, onClick }) => {
  const { t } = useTranslation('pro');

  return (
    <button
      onClick={() => onClick(workspace)}
      className="w-full text-left bg-white dark:bg-background-dark-secondary/50 border border-border-light dark:border-white/10 rounded-xl shadow-sm p-4 transition-opacity active:opacity-70"
    >
      <div className="flex items-start gap-3">
        {/* Workspace icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
          <Briefcase size={20} className="text-primary-600 dark:text-primary-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-sm text-text-primary dark:text-text-dark-primary truncate">
              {workspace.name}
            </p>
            {workspace.has_subscription === false && (
              <Lock
                size={14}
                className="flex-shrink-0 text-text-tertiary dark:text-text-dark-tertiary"
                aria-label={t('workspaces.subscriptionRequired')}
              />
            )}
          </div>

          {workspace.owner_org_name && (
            <p className="text-xs text-text-secondary dark:text-text-dark-secondary truncate mt-0.5">
              {workspace.owner_org_name}
            </p>
          )}

          {/* Badges */}
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-xs text-text-secondary dark:text-text-dark-secondary">
              <FileText size={12} className="flex-shrink-0" />
              {workspace.bill_count} {t('workspaces.billsTracked', { count: workspace.bill_count }).replace(/\d+\s/, '')}
            </span>
            {workspace.legislator_count !== undefined && (
              <span className="flex items-center gap-1 text-xs text-text-secondary dark:text-text-dark-secondary">
                <Users size={12} className="flex-shrink-0" />
                {workspace.legislator_count} {t('workspaces.legislatorsTracked', { count: workspace.legislator_count }).replace(/\d+\s/, '')}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

const ProWorkspacesPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);

  // ── Workspaces query ───────────────────────────────────────────────────────

  const { data, isLoading, isError, refetch } = useQuery(
    ['pro-workspaces'],
    async () => {
      const response = await api.get('/workspaces');
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : result?.workspaces || [];
    },
    {
      staleTime: 30000,
      onError: () => setShowError(true),
    },
  );

  const workspaces: Workspace[] = data || [];
  const isEmpty = !isLoading && workspaces.length === 0;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  const handleWorkspaceClick = (workspace: Workspace) => {
    navigate(`/pro/workspaces/${workspace.id}`);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="max-w-3xl mx-auto px-4 pb-4 pt-4 space-y-3">
          {isLoading ? (
            <ProLoadingList variant="card" count={3} className="space-y-3" />
          ) : isEmpty ? (
            <ProEmptyState
              icon={<Briefcase size={48} />}
              message={t('workspaces.emptyState')}
              description={t('workspaces.emptyStateDescription')}
            />
          ) : (
            /* Workspace list */
            <>
              {workspaces.map((workspace) => (
                <WorkspaceCard
                  key={workspace.id}
                  workspace={workspace}
                  onClick={handleWorkspaceClick}
                />
              ))}
            </>
          )}
      </div>

      {/* Error toast */}
      <IonToast
        isOpen={showError || isError}
        onDidDismiss={() => setShowError(false)}
        message={t('workspaces.errorMessage')}
        duration={4000}
        buttons={[
          {
            text: t('common.retry'),
            handler: () => {
              setShowError(false);
              refetch();
            },
          },
        ]}
        color="danger"
      />
    </>
  );
};

export default ProWorkspacesPage;
